import React, { useState, useEffect } from 'react';
import mobileImage from '../../assets/Ptrack.jpeg';

const Tracking = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [animationStage, setAnimationStage] = useState(0);
  const [activeTab, setActiveTab] = useState(0);

  const features = [
    {
      title: "Performance Analytics",
      description: "Get detailed insights into your quiz performance with visual charts and statistics that help you understand your strengths and areas for improvement.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      stats: ["87%", "92%", "65%"],
      statLabels: ["Accuracy", "Completion", "Retention"]
    },
    {
      title: "Progress Tracking",
      description: "Monitor your improvement over time with our comprehensive tracking system. Set goals and watch as you steadily achieve them through consistent practice.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      ),
      stats: ["4", "8", "6"],
      statLabels: ["Days", "Quizzes", "Hours"]
    },
    {
      title: "Insights",
      description: "Receive custom-tailored suggestions for study materials and quizzes based on your performance data, helping you focus on areas that need the most attention.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      stats: ["12", "1", "2"],
      statLabels: ["Topics", "Courses", "Hours"]
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0 }
    );

    const element = document.getElementById("tracking-section");
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);

  useEffect(() => {
    if (isVisible) {
      const timeline = [
        () => setAnimationStage(1), // Title
        () => setAnimationStage(2), // Description
        () => setAnimationStage(3), // Features
      ];

      timeline.forEach((action, index) => {
        setTimeout(() => {
          action();
        }, 400 * (index + 1));
      });
    }
  }, [isVisible]);

  return (
    <section className="py-16" id="tracking">
      <div id="tracking-section" className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          {/* Left side - Mobile Phone with Image */}
          <div className="lg:w-2/5 relative">
            <div className="bg-indigo-100 absolute w-[350px] h-[350px] rounded-full opacity-100 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></div>

            {/* Phone Frame with Image */}
            <div 
              className={`relative mx-auto transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`} 
              style={{ maxWidth: "280px" }}
            >
              <div className="relative border-8 border-indigo-100 rounded-[40px] shadow-xl overflow-hidden">
                {/* Notch */}
                <div className="absolute top-0 inset-x-0 h-6 bg-indigo-100 rounded-b-lg w-1/3 mx-auto z-10"></div>

                {/* Phone Screen with Image */}
                <div className="bg-white overflow-hidden" style={{ height: "500px" }}>
                  <img
                    src={mobileImage}
                    alt="Quiz App Interface"
                    className="w-full h-full object-contain"
                    style={{ objectPosition: 'center' }}
                  />
                </div>
              </div>

              {/* Phone Shadow */}
              <div className="absolute -bottom-4 inset-x-0 h-8 bg-gradient-to-t from-gray-900 to-transparent opacity-10 rounded-[40px] mx-auto" style={{ width: "90%" }}></div>
            </div>
          </div>

          {/* Right side - Content */}
          <div className="lg:w-3/5 mt-12 lg:mt-0 w-full">
            <div 
              className={`text-indigo-600 font-medium mb-2 transition-all duration-700 transform ${animationStage >= 1 ? 'opacity-100' : 'opacity-0'}`}
            >
              <span className="bg-indigo-100 text-indigo-600 py-1 px-3 rounded-full text-sm font-medium">Track Quiz Performance</span>
            </div>
            <h2 
              className={`text-3xl md:text-4xl font-bold mb-4 text-indigo-600 transition-all duration-700 transform ${animationStage >= 1 ? 'opacity-100' : 'opacity-0'}`}
            >
              Track Your Learning Progress
            </h2>
            <p 
              className={`text-gray-600 mb-8 transition-all duration-700 delay-100 transform ${animationStage >= 2 ? 'opacity-100' : 'opacity-0'}`}
            >
              Schedule your study sessions for times when you're most productive.
              Choose from our AI-powered recommendations, or create your own
              personalized learning schedule.
            </p>

            {/* Modern Tab Navigation */}
            <div 
              className={`mb-6 transition-all duration-700 delay-200 transform ${animationStage >= 3 ? 'opacity-100' : 'opacity-0'}`}
            >
              <div className="flex flex-wrap sm:flex-wrap gap-4 mb-6 overflow-x-auto pb-2 scrollbar-hide">
                {features.map((feature, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveTab(index)}
                    className={`px-4 py-2 sm:px-4 sm:py-2 rounded-full transition-all duration-300 whitespace-nowrap text-sm sm:text-base ${activeTab === index 
                      ? 'bg-indigo-600 text-white shadow-md' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    <div className="flex items-center space-x-2 sm:space-x-2 gap-1">
                      <div className="w-5 h-5 sm:w-5 sm:h-5">{feature.icon}</div>
                      <span className="truncate">{feature.title}</span>
                    </div>
                  </button>
                ))}
              </div>

              {/* Feature Content */}
              <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 transition-all duration-500 transform">
                <div className="flex flex-col sm:flex-row items-start mb-4">
                  <div className="bg-indigo-100 text-indigo-600 rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mb-3 sm:mb-0 sm:mr-4 flex-shrink-0">
                    {features[activeTab].icon}
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-lg sm:text-xl">{features[activeTab].title}</h3>
                    <p className="text-gray-600 text-sm sm:text-base">
                      {features[activeTab].description}
                    </p>
                  </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-3 gap-2 sm:gap-4 mt-6">
                  {features[activeTab].stats.map((stat, index) => (
                    <div key={index} className="bg-indigo-50 rounded-lg p-2 sm:p-4 text-center transition-all duration-300 hover:shadow-md hover:bg-indigo-100">
                      <div className="text-xl sm:text-2xl font-bold text-indigo-600">{stat}</div>
                      <div className="text-xs sm:text-sm text-gray-600 mt-1">{features[activeTab].statLabels[index]}</div>
                    </div>
                  ))}
                </div>

                {/* Action Button */}
                <div className="mt-6">
                  <button className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white px-4 sm:px-6 py-2 rounded-lg transition-colors duration-300 flex items-center justify-center sm:justify-start">
                    <span>Start Tracking</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Tracking;
